/**
 * Route Configuration
 * 
 * This file centralizes all route definitions to make them easily configurable.
 * Change APP_BASE_PATH to modify where the app is served from.
 */

// Base path for the application (without trailing slash)
// Force /app for now since env var is set to /
export const APP_BASE_PATH = '/app';

// Ensure no trailing slash
const basePath = APP_BASE_PATH.replace(/\/$/, '');

// Debug
console.log('[ROUTES] process.env.REACT_APP_BASE_PATH:', process.env.REACT_APP_BASE_PATH);
console.log('[ROUTES] APP_BASE_PATH:', APP_BASE_PATH);
console.log('[ROUTES] basePath after replace:', basePath);

/**
 * Route builder utility
 * @param {string} path - The path relative to the app base
 * @returns {string} - The full path with app base
 */
export const buildRoute = (path = '') => {
  // Remove leading slash from path if present
  const cleanPath = path.replace(/^\//, '');
  
  // If no path provided, return just the base
  if (!cleanPath) {
    return basePath || '/';
  }
  
  // Combine base path with the specific path
  return `${basePath}/${cleanPath}`;
};

/**
 * Pre-built common routes
 */
export const ROUTES = {
  // App base
  APP_HOME: buildRoute(),
  
  // Auth routes
  LOGIN: buildRoute('login'),
  REGISTER: buildRoute('register'),
  FORGOT_PASSWORD: buildRoute('forgot-password'),
  RESET_PASSWORD: buildRoute('reset-password'),
  VERIFY_EMAIL: buildRoute('verify-email'),
  
  // Main app routes
  FIND: buildRoute('find'),
  ADD: buildRoute('add'),
  ADD_ITEM: buildRoute('add-item'),
  CREATE_BOX: buildRoute('create-box'),
  ALL_ITEMS: buildRoute('all-items'),
  ALL_BOXES: buildRoute('all-boxes'),
  OFFLINE_ITEMS: buildRoute('offline-items'),
  SETTINGS: buildRoute('settings'),
  SUBSCRIPTION: buildRoute('subscription'),
  PRO_FEATURES: buildRoute('pro-features'),
  COMING_SOON: buildRoute('coming-soon'),
  PROFILE: buildRoute('profile'),
  TAGS: buildRoute('tags'),
  LOCATIONS: buildRoute('locations'),
  IMAGE_TEST: buildRoute('image-test'),
  ASSIGN_QR: buildRoute('assign-qr'),
  TERMS_OF_SERVICE: buildRoute('terms'),
  PRIVACY_POLICY: buildRoute('privacy'),
  DATA_DELETION: buildRoute('data-deletion'),
  
  // Let Go / Exit Bin routes
  LET_GO: buildRoute('let-go'),
  EXIT_BIN: buildRoute('exit-bin'),
  
  // Admin routes
  ADMIN_SUPPORT_TICKET: (ticketId) => buildRoute(`admin/support/${ticketId}`),
  ADMIN_SUPPORT_PATTERN: buildRoute('admin/support/:ticketId'),
  ADMIN_KB: buildRoute('admin/kb'),
  ADMIN_KB_EDIT: (entryId) => buildRoute(`admin/kb/${entryId}`),
  ADMIN_KB_EDIT_PATTERN: buildRoute('admin/kb/:entryId'),
  
  // Dynamic routes (functions)
  BOX_DETAIL: (boxId) => buildRoute(`box/${boxId}`),
  BOX_DETAIL_UUID: (uuid) => buildRoute(`box/uuid/${uuid}`),
  BOX_EDIT: (boxId) => buildRoute(`box/${boxId}/edit`),
  ITEM_EDIT: (itemId) => buildRoute(`item/${itemId}`),
  RESET_PASSWORD_TOKEN: (token) => buildRoute(`reset-password/${token}`),
  VERIFY_EMAIL_TOKEN: (token) => buildRoute(`verify-email/${token}`),
  FAMILY_ACCEPT_INVITATION: (token) => buildRoute(`family/accept/${token}`),
  
  // Route patterns for React Router (with :param syntax)
  FAMILY_ACCEPT_PATTERN: buildRoute('family/accept/:token'),
  BOX_UUID_PATTERN: buildRoute('box/uuid/:uuid'),
};

// Debug: Log the family accept pattern
console.log('[ROUTES] FAMILY_ACCEPT_PATTERN:', ROUTES.FAMILY_ACCEPT_PATTERN);
console.log('[ROUTES] APP_HOME:', ROUTES.APP_HOME);

/**
 * Check if a path is within the app base
 * @param {string} pathname - The pathname to check
 * @returns {boolean} - True if the path is within the app
 */
export const isAppRoute = (pathname) => {
  if (basePath === '' || basePath === '/') {
    return true; // If no base path, all routes are app routes
  }
  return pathname.startsWith(basePath);
};

/**
 * Get the app-relative path from a full pathname
 * @param {string} pathname - The full pathname
 * @returns {string} - The path relative to the app base
 */
export const getAppRelativePath = (pathname) => {
  if (basePath === '' || basePath === '/') {
    return pathname;
  }
  return pathname.replace(basePath, '') || '/';
};

export default ROUTES;
